// Managers recruteurs — accès limité au module Recrutement.
//
// Un compte RECRUTEUR n'atteint que /recrutement, et seulement les
// postes dont il est le manager recruteur (le serveur filtre : demandes,
// pipeline, candidatures, onboarding). Une seule liste, par personne :
//   1. ajouter la personne (email Google) ou suspendre son accès ;
//   2. lui confier un poste en cours (« Confier un poste ») ou le lui
//      retirer — un poste a toujours un manager, donc « retirer » le
//      rend à vous (le compte connecté), dit explicitement sur le bouton.

import { useMemo, useState } from 'react';
import { Plus, X } from 'lucide-react';
import { ENTITIES } from '@pointage/shared';
import { toast } from '../../store/toast.js';
import {
  useInviteUser, useMe, useRecHiringManager, useRecRequisitions, useUpdateUser, useUsers,
  type RecReqStatus, type RecRequisitionDTO, type UserDTO,
} from '../../api/hooks.js';

const ST_LABEL: Record<RecReqStatus, string> = {
  brouillon: 'Brouillon', en_attente: 'En validation', validee: 'Validée', ouverte: 'Ouverte',
  gelee: 'Gelée', pourvue: 'Pourvue', annulee: 'Annulée',
};
// Ouverts d'abord : c'est là que le manager recruteur travaille.
const ST_ORDER: Record<RecReqStatus, number> = {
  ouverte: 0, validee: 1, en_attente: 2, brouillon: 3, gelee: 4, pourvue: 5, annulee: 6,
};
const initials = (name: string) =>
  name.trim().split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]?.toUpperCase() ?? '').join('') || '?';
const entName = (id: string) => ENTITIES.find((e) => e.id === id)?.name ?? id;
const fmtLogin = (iso: string | null) =>
  iso ? `connecté le ${new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })}` : 'jamais connecté';

function StatusPill({ status }: { status: RecReqStatus }) {
  return <span className={`rr-st st-${status}`}>{ST_LABEL[status]}</span>;
}

export function RecruitersCard({ ping }: { ping: () => void }) {
  const me = useMe();
  const perms = me.data?.permissions ?? [];
  const meId = me.data?.user.id ?? null;
  const canManage = perms.includes('users.manage');
  const canAssign = perms.includes('recrutement.offre');
  const users = useUsers();
  const reqs = useRecRequisitions('all');
  const invite = useInviteUser();
  const update = useUpdateUser();
  const assign = useRecHiringManager();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');

  const all = users.data ?? [];
  const recruiters = all.filter((u) => u.role === 'RECRUTEUR');
  const recruiterIds = new Set(recruiters.map((u) => u.id));
  // Qui peut reprendre un poste retiré : le compte connecté, s'il peut mener un recrutement.
  const meUser = all.find((u) => u.id === meId);
  const canTakeBack = !!meUser && meUser.active && meUser.permissions.includes('recrutement.edit');
  const live = useMemo(
    () => (reqs.data ?? [])
      .filter((r) => r.status !== 'pourvue' && r.status !== 'annulee')
      .sort((a, b) => ST_ORDER[a.status] - ST_ORDER[b.status] || a.roleLabel.localeCompare(b.roleLabel, 'fr')),
    [reqs.data],
  );
  const postsOf = (u: UserDTO) => live.filter((r) => r.hiringManager?.id === u.id);
  const unassigned = live.filter((r) => !r.hiringManager || !recruiterIds.has(r.hiringManager.id));

  const doInvite = () => {
    const mail = email.trim().toLowerCase();
    if (!mail) return;
    invite.mutate({ email: mail, name: name.trim() || undefined, role: 'RECRUTEUR' }, {
      onSuccess: () => { setName(''); setEmail(''); ping(); },
      onError: (e) => {
        const code = (e as Error & { body?: { error?: string } }).body?.error;
        toast.danger(code === 'user_exists'
          ? 'Ce compte existe déjà — changez son rôle dans Accès & utilisateurs.'
          : 'Ajout impossible — vérifiez l’email.');
      },
    });
  };
  const setActive = (u: UserDTO, active: boolean) => update.mutate({ id: u.id, active }, {
    onSuccess: ping, onError: () => toast.danger('Échec de l’enregistrement.'),
  });
  const setManager = (reqId: string, userId: string) => assign.mutate({ id: reqId, userId }, {
    onSuccess: ping,
    onError: (e) => toast.danger((e as { body?: { message?: string } }).body?.message ?? 'Échec de l’enregistrement.'),
  });

  if (!users.data) return <div className="scard-body"><p className="srow-help" style={{ padding: 16 }}>Chargement…</p></div>;

  const optionLabel = (r: RecRequisitionDTO) =>
    `${r.roleLabel} · ${entName(r.entityId)} — ${ST_LABEL[r.status].toLowerCase()}${r.hiringManager ? ` · mené par ${r.hiringManager.name}` : ''}`;

  return (
    <div className="scard-body" data-testid="recruiters-card">
      <div className="rr-scope">
        <span className="rr-scope-k">Il voit</span>
        <span>le pipeline des postes confiés — présélection, appel, cas pratique, entretiens — et les points d’essai de la recrue.</span>
        <span className="rr-scope-k off">Reste à vous</span>
        <span>demandes, vérification, offre, salaires et tous les autres modules.</span>
      </div>

      {canManage && (
        <div className="rr-add" data-testid="rr-invite">
          <div className="rr-add-label">Ajouter un manager recruteur <span>· actif dès sa première connexion Google</span></div>
          <div className="rr-add-row">
            <input className="finput" placeholder="Prénom Nom" value={name} onChange={(e) => setName(e.target.value)}
              aria-label="Nom du manager recruteur" data-testid="rr-name" />
            <input className="finput" type="email" placeholder="email@exemple.com" value={email}
              onChange={(e) => setEmail(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') doInvite(); }}
              aria-label="Email Google du manager recruteur" data-testid="rr-email" />
            <button className="btn btn-primary" onClick={doInvite} disabled={invite.isPending || !email.trim()} data-testid="rr-add">
              <Plus /> Ajouter
            </button>
          </div>
        </div>
      )}

      <div className="rr-list" data-testid="rr-list">
        {recruiters.length === 0 && <p className="srow-help rr-empty">Aucun manager recruteur pour l’instant.</p>}
        {recruiters.map((u) => {
          const posts = postsOf(u);
          const pickable = live.filter((r) => r.hiringManager?.id !== u.id);
          return (
            <div key={u.id} className={'rr-card' + (u.active ? '' : ' off')} data-testid={`rr-${u.email}`}>
              <div className="rr-card-head">
                <span className="rr-av">{initials(u.name)}</span>
                <div className="rr-id">
                  <div className="rr-name">{u.name}{!u.active && <span className="rr-tag">accès suspendu</span>}</div>
                  <div className="srow-help">{u.email} · {fmtLogin(u.lastLoginAt)}</div>
                </div>
                {canManage && (
                  <button className="btn btn-sm btn-ghost" onClick={() => setActive(u, !u.active)} data-testid={`rr-toggle-${u.email}`}>
                    {u.active ? 'Suspendre' : 'Réactiver'}
                  </button>
                )}
              </div>

              <div className="rr-posts" data-testid={`rr-posts-${u.email}`}>
                <div className="rr-posts-k">Postes confiés</div>
                {posts.length === 0 && (
                  <div className="rr-none">Aucun poste — il ne voit rien pour l’instant.</div>
                )}
                {posts.map((r) => (
                  <div key={r.id} className="rr-post" data-testid={`rr-post-${r.id}`}>
                    <span className="rr-post-name">{r.roleLabel}</span>
                    <span className="rr-post-meta">{entName(r.entityId)}</span>
                    <StatusPill status={r.status} />
                    {canAssign && (
                      <button className="rr-post-x" disabled={!canTakeBack || assign.isPending}
                        onClick={() => meId && setManager(r.id, meId)}
                        title={canTakeBack ? `Retirer — le poste vous revient (${meUser!.name})` : 'Confiez ce poste à quelqu’un d’autre pour le retirer'}
                        aria-label={`Retirer ${r.roleLabel} à ${u.name}`} data-testid={`rr-unassign-${r.id}`}>
                        <X />
                      </button>
                    )}
                  </div>
                ))}
                {canAssign && u.active && pickable.length > 0 && (
                  <select className="finput rr-pick" value="" disabled={assign.isPending}
                    onChange={(e) => e.target.value && setManager(e.target.value, u.id)}
                    aria-label={`Confier un poste à ${u.name}`} data-testid={`rr-pick-${u.email}`}>
                    <option value="">+ Confier un poste…</option>
                    {pickable.map((r) => <option key={r.id} value={r.id}>{optionLabel(r)}</option>)}
                  </select>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {live.length > 0 && (
        <div className="rr-free" data-testid="rr-unassigned">
          <div className="rr-posts-k">Postes en cours sans manager recruteur</div>
          {unassigned.length === 0
            ? <div className="rr-none">Tous les postes en cours sont confiés.</div>
            : unassigned.map((r) => (
              <div key={r.id} className="rr-free-row">
                <span className="rr-post-name">{r.roleLabel}</span>
                <span className="rr-post-meta">{entName(r.entityId)}</span>
                <StatusPill status={r.status} />
                <span className="rr-free-who">{r.hiringManager ? `mené par ${r.hiringManager.name}` : 'à désigner'}</span>
              </div>
            ))}
        </div>
      )}
    </div>
  );
}
